import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Info } from 'lucide-react';
import { Seo } from '../../components/seo/Seo';
import { PublicLayout } from '../../components/public/PublicLayout';
import { Container } from '../../components/public/PublicSections';
import { ContentBlocks, TableOfContents } from '../../components/public/ContentBlocks';
import { PRIVACY, TERMS, LEGAL_COPY } from '../../seo/legalContent';
import { LAYOUT_COPY } from '../../seo/publicContent';
import { pageMeta, pagePath, alternatesFor, LEGAL_UPDATED, PUBLISHER, LEGAL_EMAIL } from '../../seo/seoConfig.mjs';
import { breadcrumbSchema } from '../../seo/structuredData';

const DOCS = { privacy: PRIVACY, terms: TERMS };
const formatDate = (iso, lang) => new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });

/** Shared renderer for the privacy policy and the terms of service (FR/EN). */
export default function PublicLegalPage({ lang, doc }) {
  const content = DOCS[doc][lang];
  const copy = LEGAL_COPY[lang];
  const meta = pageMeta(doc, lang);
  const nav = LAYOUT_COPY[lang].nav;
  const otherDoc = doc === 'privacy' ? 'terms' : 'privacy';
  const hasPlaceholders = /\[(?:\u00c0 COMPL|TO BE COMPL)/.test(JSON.stringify(content));

  return (
    <PublicLayout lang={lang} pageKey={doc}>
      <Seo
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        path={pagePath(doc, lang)}
        lang={lang}
        alternates={alternatesFor(doc)}
        jsonLd={[breadcrumbSchema([
          { name: nav.home, path: pagePath('home', lang) },
          { name: nav[doc], path: pagePath(doc, lang) },
        ])]}
      />

      <header className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(600px circle at 15% 0%, rgba(243,179,61,0.10), transparent 55%)' }} />
        <Container className="relative pt-14 pb-10 sm:pt-20 sm:pb-12 max-w-4xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--lux-gold)] mb-3">{content.eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.08] text-white" data-testid={`legal-title-${doc}`}>{content.h1}</h1>
          <p className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed">{content.intro}</p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/45" data-testid="legal-meta">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[var(--lux-gold)]" /> {copy.updatedLabel} {formatDate(LEGAL_UPDATED, lang)}</span>
            <span>{copy.publisherLabel} : {PUBLISHER}</span>
            <a href={`mailto:${LEGAL_EMAIL}`} className="hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid="legal-contact-email">{copy.contactLabel} : {LEGAL_EMAIL}</a>
          </div>
        </Container>
      </header>

      <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16">
        <aside className="lg:col-span-4 order-first lg:order-last">
          <div className="lg:sticky lg:top-24 space-y-4">
            <TableOfContents title={copy.tocTitle} sections={content.sections} testId={`legal-toc-${doc}`} />
            <div className="rounded-2xl border border-white/10 bg-lux-panel/90 p-5">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-3">{copy.seeAlso}</p>
              <Link to={pagePath(otherDoc, lang)} className="text-sm text-white/70 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid="legal-other-doc-link">{nav[otherDoc]}</Link>
            </div>
          </div>
        </aside>
        <div className="lg:col-span-8 max-w-3xl" data-testid={`legal-body-${doc}`}>
          {hasPlaceholders && (
            <p className="mb-6 flex items-start gap-2 text-xs text-white/45 rounded-xl border border-white/10 bg-lux-panel/90 px-4 py-3" data-testid="legal-placeholder-note">
              <Info className="w-4 h-4 text-[var(--lux-amber)] shrink-0 mt-0.5" aria-hidden="true" /> {copy.placeholderNote}
            </p>
          )}
          {content.sections.map(section => (
            <section key={section.id} id={section.id} className="scroll-mt-24 pt-8 first:pt-0" aria-labelledby={`${section.id}-title`} data-testid={`legal-section-${section.id}`}>
              <h2 id={`${section.id}-title`} className="font-display text-2xl text-white">{section.title}</h2>
              <ContentBlocks blocks={section.blocks} testIdPrefix={`legal-${section.id}`} />
            </section>
          ))}
        </div>
      </Container>
    </PublicLayout>
  );
}
